import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table'
import { 
  FileText, 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  CheckCircle, 
  XCircle,
  MessageCircle,
  Loader,
  RefreshCw,
  Trash2
} from 'lucide-react'
import { Bid, Rfq, BidType, BidStatus, RfqType, RfqStatus } from '@/types'
import { formatCurrency, formatDate } from '@/utils'
import { tradingService } from '@/services/tradingService'
import { ConfirmationModal } from '@/components/ui/confirmation-modal'

interface InquiriesTableProps {
  className?: string
}

type InquiryItem = (Bid & { inquiryType: 'bid' }) | (Rfq & { inquiryType: 'rfq' })

const InquiriesTable: React.FC<InquiriesTableProps> = ({ className }) => {
  const [inquiries, setInquiries] = useState<InquiryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean
    inquiry: InquiryItem | null
    loading: boolean
  }>({
    isOpen: false,
    inquiry: null,
    loading: false
  })

  useEffect(() => {
    const fetchInquiries = async () => {
      setLoading(true)
      try {
        // Fetch both bids and RFQs
        const [bidsResponse, rfqsResponse] = await Promise.all([
          tradingService.getUserBids(),
          tradingService.getUserRfqs()
        ])

        // Combine and transform the data
        const combinedInquiries: InquiryItem[] = [
          ...bidsResponse.data.map(bid => ({ ...bid, inquiryType: 'bid' as const })),
          ...rfqsResponse.data.map(rfq => ({ ...rfq, inquiryType: 'rfq' as const }))
        ]

        // Sort by creation date (newest first)
        combinedInquiries.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )

        setInquiries(combinedInquiries)
      } catch (err) {
        console.error('Failed to fetch inquiries:', err)
        setError('Failed to load inquiries')
      } finally {
        setLoading(false)
      }
    }

    fetchInquiries()
  }, [])

  const getStatusBadge = (inquiry: InquiryItem) => {
    if (inquiry.inquiryType === 'rfq') {
      const status = inquiry.status
      switch (status) {
        case RfqStatus.PENDING:
          return <Badge variant="outline" className="text-yellow-600 border-yellow-600"><Clock size={12} className="mr-1" />Pending</Badge>
        case RfqStatus.ACCEPTED:
          return <Badge variant="outline" className="text-green-600 border-green-600"><CheckCircle size={12} className="mr-1" />Accepted</Badge>
        case RfqStatus.REJECTED:
          return <Badge variant="outline" className="text-red-600 border-red-600"><XCircle size={12} className="mr-1" />Rejected</Badge>
        case RfqStatus.CONTACTED:
          return <Badge variant="outline" className="text-blue-600 border-blue-600"><MessageCircle size={12} className="mr-1" />Contacted</Badge>
        default:
          return <Badge variant="outline">Unknown</Badge>
      }
    } else if (inquiry.inquiryType === 'bid') {
      // Normalize status in case API returns a plain string or different casing
      const rawStatus = (inquiry as any).status
      const status = typeof rawStatus === 'string' ? rawStatus.toUpperCase() : rawStatus
      switch (status) {
        case BidStatus.PENDING:
        case 'PENDING':
          return <Badge variant="outline" className="text-yellow-600 border-yellow-600"><Clock size={12} className="mr-1" />Pending</Badge>
        case BidStatus.ACCEPTED:
        case 'ACCEPTED':
          return <Badge variant="outline" className="text-green-600 border-green-600"><CheckCircle size={12} className="mr-1" />Accepted</Badge>
        case BidStatus.REJECTED:
        case 'REJECTED':
          return <Badge variant="outline" className="text-red-600 border-red-600"><XCircle size={12} className="mr-1" />Rejected</Badge>
        case BidStatus.CONTACTED:
        case 'CONTACTED':
          return <Badge variant="outline" className="text-blue-600 border-blue-600"><MessageCircle size={12} className="mr-1" />Contacted</Badge>
        default:
          return <Badge variant="outline" className="text-blue-600 border-blue-600">Bid</Badge>
      }
    }
    return <Badge variant="outline" className="text-blue-600 border-blue-600">Bid</Badge>
  }

  const getTypeIcon = (inquiry: InquiryItem) => {
    const type = inquiry.type
    if (type === BidType.BUY || type === RfqType.BUY) {
      return <TrendingUp className="text-green-500" size={16} />
    }
    return <TrendingDown className="text-red-500" size={16} />
  }

  const getTypeLabel = (inquiry: InquiryItem) => {
    const type = inquiry.type
    return type === BidType.BUY || type === RfqType.BUY ? 'Buy' : 'Sell'
  }

  const handleDeleteClick = (inquiry: InquiryItem) => {
    setDeleteModal({
      isOpen: true,
      inquiry,
      loading: false
    })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteModal.inquiry) return

    setDeleteModal(prev => ({ ...prev, loading: true }))

    try {
      // TODO: Implement delete API call based on inquiry type
      // if (deleteModal.inquiry.inquiryType === 'bid') {
      //   await tradingService.deleteBid(deleteModal.inquiry.inquiry_id)
      // } else {
      //   await tradingService.deleteRfq(deleteModal.inquiry.rfq_id)
      // }
      
      // For now, just remove from local state
      setInquiries(prev => prev.filter(item => {
        if (deleteModal.inquiry!.inquiryType === 'bid' && item.inquiryType === 'bid') {
          return item.inquiry_id !== deleteModal.inquiry!.inquiry_id
        } else if (deleteModal.inquiry!.inquiryType === 'rfq' && item.inquiryType === 'rfq') {
          return item.rfq_id !== deleteModal.inquiry!.rfq_id
        }
        return true
      }))

      setDeleteModal({
        isOpen: false,
        inquiry: null,
        loading: false
      })
    } catch (err) {
      console.error('Failed to delete inquiry:', err)
      setError('Failed to delete inquiry')
      setDeleteModal(prev => ({ ...prev, loading: false }))
    }
  }

  const handleDeleteCancel = () => {
    setDeleteModal({
      isOpen: false,
      inquiry: null,
      loading: false
    })
  }

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-8">
          <Loader className="animate-spin mr-2" size={20} />
          <span>Loading inquiries...</span>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-center">
            <XCircle className="text-red-500 mx-auto mb-2" size={24} />
            <p className="text-red-500 mb-2">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline" size="sm">
              <RefreshCw size={16} className="mr-1" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText size={20} />
          My Inquiries
        </CardTitle>
      </CardHeader>
      <CardContent>
        {inquiries.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="mx-auto mb-4 text-gray-400" size={48} />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No inquiries yet</h3>
            <p className="text-gray-500">Your RFQs and bids will appear here once you create them.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Valuation</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inquiries.map((inquiry) => (
                  <TableRow key={inquiry.inquiryType === 'bid' ? inquiry.inquiry_id : inquiry.rfq_id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getTypeIcon(inquiry)}
                        <span className="font-medium">{getTypeLabel(inquiry)}</span>
                        <Badge variant="secondary" className="text-xs">
                          {inquiry.inquiryType === 'bid' ? 'Bid' : 'RFQ'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {inquiry.logo && (
                          <img 
                            src={inquiry.logo} 
                            alt={inquiry.project_name}
                            className="w-6 h-6 rounded-full object-cover"
                          />
                        )}
                        <span className="font-medium">{inquiry.project_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{formatCurrency(inquiry.tranche_size)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{formatCurrency(inquiry.valuation)}</span>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(inquiry)}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600">
                        {formatDate(inquiry.created_at)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDeleteClick(inquiry)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Inquiry"
        message={`Are you sure you want to delete this ${deleteModal.inquiry?.inquiryType === 'bid' ? 'bid' : 'RFQ'}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleteModal.loading}
      />
    </Card>
  )
}

export default InquiriesTable
